import type { LegacyCaseDetail, LegacyPatientData } from "../types/legacyCases";
import { parseDateOnly } from "./dateOnly";

interface SourceSelection {
  sourceRowId: string;
  reportDocumentId: string;
  extractionId: string;
}

function comparisonValue(field: string, value: string): string {
  const text = value.trim().replace(/\s+/g, " ").toUpperCase();
  if (field === "dateOfBirth") {
    const usDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
    const date = usDate
      ? `${usDate[3]}-${usDate[1].padStart(2, "0")}-${usDate[2].padStart(
          2,
          "0"
        )}`
      : text;
    return parseDateOnly(date) || text;
  }
  if (field === "sex") {
    if (text === "MALE") return "M";
    if (text === "FEMALE") return "F";
  }
  return text.replace(/\s*,\s*/g, ", ");
}

export function legacyPatientComparison(
  detail: LegacyCaseDetail,
  selection: SourceSelection,
  patient: LegacyPatientData
) {
  const source = detail.sourceRows.find(
    (row) => row.id === selection.sourceRowId
  )?.normalized;
  const report = detail.extractions.find(
    (entry) =>
      entry.id === selection.extractionId &&
      entry.documentId === selection.reportDocumentId
  );
  const fields = [
    {
      field: "patientName",
      label: "Patient name",
      spreadsheet: source
        ? `${source.lastName}, ${[source.firstName, source.middleInitial]
            .filter(Boolean)
            .join(" ")}`
        : "",
      current: `${patient.lastName}, ${patient.firstName}`,
    },
    {
      field: "dateOfBirth",
      label: "Date of birth",
      spreadsheet: source?.dateOfBirth || "",
      current: patient.dateOfBirth,
    },
    {
      field: "sex",
      label: "Sex",
      spreadsheet: source?.sexRaw || "",
      current: patient.sex,
    },
    {
      field: "medicalRecordNumber",
      label: "Medical record number",
      spreadsheet: "",
      current: patient.medicalRecordNumber || "",
    },
  ];
  return fields.map((field) => {
    const reportValues = Array.from(
      new Set(
        report?.result.fields[field.field]?.map((value) => value.rawValue) || []
      )
    );
    const evidence = [field.spreadsheet, ...reportValues].filter((value) =>
      value.trim()
    );
    return {
      ...field,
      reportValues,
      differs: evidence.some(
        (value) =>
          comparisonValue(field.field, value) !==
          comparisonValue(field.field, field.current)
      ),
    };
  });
}
